// Brand Console GROWTH -- Outcomes.
//
// Reuses the existing, already-real AF-2.8 OutcomeService entirely
// through its existing GET /intelligence/{workspace_id}/outcomes route
// (outcomesAPI.listForWorkspace, added this phase for the exact same
// reason recommendationsAPI/opportunitiesAPI.listForWorkspace were added
// earlier -- the route already existed, fully filterable, with zero
// frontend consumer). No new backend endpoint, no new service method, no
// new collection.
//
// Every field this page renders comes directly from OutcomeService's own
// deterministic evaluate_outcome() output (services.py, verified by
// direct read): outcome_type/status/result/success are pure functions of
// real, already-recorded measurement values -- no statistical
// significance is computed or implied, matching the service's own
// documented discipline. "Entity", "Action plan", "Recommendation" and
// "Opportunity" are the outcome's own real *_id fields, resolved against
// the workspace's own entities/action-plans/recommendations/opportunities
// lists (the same id-resolution pattern RecommendationsPage.js already
// uses for opportunity_id/source_signal_ids) -- never guessed, and shown
// as the raw id when a resolution genuinely isn't available.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { StatusBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { MetricCard } from '@/components/brand/MetricCard';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { outcomesAPI, entitiesAPI, actionPlansAPI, recommendationsAPI, opportunitiesAPI } from '@/lib/intelligenceApi';

function dash(v) {
  return v === null || v === undefined || v === '' ? '—' : v;
}
function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
function sortRows(rows, key, dir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
function SortHeader({ label, col, sort, onSort, testId }) {
  return (
    <th className="px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => onSort(col)} data-testid={testId}>
      {label}{sort.key === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

export default function OutcomesPage() {
  // P1.9: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [outcomes, setOutcomes] = useState(null); // null = loading
  const [entityNames, setEntityNames] = useState({});
  const [actionPlanById, setActionPlanById] = useState({});
  const [recommendationById, setRecommendationById] = useState({});
  const [opportunityById, setOpportunityById] = useState({});
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ result: '', status: '' });
  const [appliedFilters, setAppliedFilters] = useState({ result: '', status: '' });

  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selected, setSelected] = useState(null);

  const load = useCallback((activeFilters) => {
    if (!clientId) return;
    setOutcomes(null);
    setError(null);
    const params = {};
    if (activeFilters.result) params.result = activeFilters.result;
    if (activeFilters.status) params.status = activeFilters.status;
    Promise.all([
      outcomesAPI.listForWorkspace(clientId, params),
      entitiesAPI.list(clientId, { includeArchived: true }).catch(() => ({ data: { entities: [] } })),
      actionPlansAPI.listForWorkspace(clientId).catch(() => ({ data: { action_plans: [] } })),
      recommendationsAPI.listForWorkspace(clientId).catch(() => ({ data: { recommendations: [] } })),
      opportunitiesAPI.listForWorkspace(clientId).catch(() => ({ data: { opportunities: [] } })),
    ])
      .then(([outRes, entRes, planRes, recRes, oppRes]) => {
        setOutcomes(outRes.data?.outcomes || []);
        const names = {};
        (entRes.data?.entities || []).forEach((e) => { names[e.id] = e.canonical_name || e.id; });
        setEntityNames(names);
        const plans = {};
        (planRes.data?.action_plans || []).forEach((p) => { plans[p.id] = p; });
        setActionPlanById(plans);
        const recs = {};
        (recRes.data?.recommendations || []).forEach((r) => { recs[r.id] = r; });
        setRecommendationById(recs);
        const opps = {};
        (oppRes.data?.opportunities || []).forEach((o) => { opps[o.id] = o; });
        setOpportunityById(opps);
      })
      .catch((e) => setError(e));
  }, [clientId]);

  useEffect(() => { load(appliedFilters); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
    load(filters);
  };
  const clearFilters = () => {
    const cleared = { result: '', status: '' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    load(cleared);
  };

  const sortedRows = useMemo(() => (outcomes ? sortRows(outcomes, sort.key, sort.dir) : []), [outcomes, sort]);
  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  };

  const planLabel = (id) => (id && actionPlanById[id]?.title) || id || null;
  const recLabel = (id) => (id && recommendationById[id]?.title) || id || null;
  const oppLabel = (id) => (id && opportunityById[id]?.title) || id || null;

  return (
    <DashboardLayout>
      <div data-testid="outcomes-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Outcomes</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Real, deterministically-evaluated results of action plans -- computed only from actual recorded measurements, never a statistical estimate.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && <EmptyState title="ADD A BRAND FIRST" description="Outcomes appear once Martian has a workspace with evaluated action plans." />}

        {!noClient && outcomes === null && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={() => load(appliedFilters)} />}

        {!noClient && !error && outcomes !== null && (
          <>
            <form onSubmit={applyFilters} className="flex items-end gap-2 flex-wrap mb-4" data-testid="outcomes-filters">
              <div>
                <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Result</label>
                <Select value={filters.result || 'ANY'} onValueChange={(v) => setFilters((f) => ({ ...f, result: v === 'ANY' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-outcome-result" className="h-8 w-[160px] text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY">Any</SelectItem>
                    <SelectItem value="POSITIVE">Positive</SelectItem>
                    <SelectItem value="NEGATIVE">Negative</SelectItem>
                    <SelectItem value="NEUTRAL">Neutral</SelectItem>
                    <SelectItem value="INCONCLUSIVE">Inconclusive</SelectItem>
                    <SelectItem value="INSUFFICIENT_DATA">Insufficient data</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Status</label>
                <Select value={filters.status || 'ANY'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'ANY' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-outcome-status" className="h-8 w-[150px] text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY">Any</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="EVALUATED">Evaluated</SelectItem>
                    <SelectItem value="INCONCLUSIVE">Inconclusive</SelectItem>
                    <SelectItem value="SUPERSEDED">Superseded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" size="sm" data-testid="apply-outcome-filters" className="h-8 text-[12px]">Apply</Button>
              <Button type="button" variant="outline" size="sm" data-testid="clear-outcome-filters" className="h-8 text-[12px]" onClick={clearFilters}>Clear filters</Button>
            </form>

            {outcomes.length === 0 && (
              <EmptyState
                testId="outcomes-empty"
                title="No outcomes yet"
                description="Martian records an outcome once an action plan's success metric has real baseline and observed measurements to compare -- never before real evidence exists."
              />
            )}

            {outcomes.length > 0 && (
              <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="outcomes-table">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Entity</th>
                      <th className="px-3 py-2">Primary metric</th>
                      <SortHeader label="Result" col="result" sort={sort} onSort={toggleSort} testId="sort-outcome-result" />
                      <SortHeader label="Status" col="status" sort={sort} onSort={toggleSort} testId="sort-outcome-status" />
                      <th className="px-3 py-2">Confidence</th>
                      <SortHeader label="Created" col="created_at" sort={sort} onSort={toggleSort} testId="sort-outcome-created" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((o) => (
                      <tr
                        key={o.id} data-testid={`outcome-row-${o.id}`}
                        className="border-b border-[var(--border-light,var(--border))] last:border-b-0 cursor-pointer hover:bg-[var(--surface-sunken,var(--canvas-secondary))] focus-within:bg-[var(--surface-sunken,var(--canvas-secondary))]"
                        onClick={() => setSelected(o)}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') setSelected(o); }}
                      >
                        <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(o.outcome_type)}</td>
                        <td className="px-3 py-2 text-[var(--text-primary)]">{dash(entityNames[o.entity_id] || o.entity_id)}</td>
                        <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{dash(o.primary_metric)}</td>
                        <td className="px-3 py-2">{o.result ? <StatusBadge value={o.result} /> : '—'}</td>
                        <td className="px-3 py-2">{o.status ? <StatusBadge value={o.status} /> : '—'}</td>
                        <td className="px-3 py-2"><ConfidenceIndicator value={o.confidence} /></td>
                        <td className="px-3 py-2 text-[var(--text-tertiary)] font-mono whitespace-nowrap">{formatDateTime(o.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── Outcome detail ── */}
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="outcome-detail">
            {selected && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-[var(--text-primary)] text-[14px]">{dash(entityNames[selected.entity_id] || selected.entity_id)}</SheetTitle>
                  <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored outcome record.</SheetDescription>
                </SheetHeader>
                <div className="mt-3 space-y-3 text-[12px]">
                  <MetricCard
                    label={selected.primary_metric || 'Primary metric'}
                    baseline={{ baseline_status: selected.baseline_value !== null && selected.baseline_value !== undefined ? 'PROVIDED' : 'MISSING', baseline_value: selected.baseline_value }}
                    current={{ current_value: selected.observed_value, direction: selected.direction, delta: selected.delta, delta_pct: selected.delta_pct }}
                    status={selected.result}
                  />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Outcome type</p><p className="text-[var(--text-primary)]">{dash(selected.outcome_type)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Status</p><p>{selected.status ? <StatusBadge value={selected.status} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Target direction</p><p className="text-[var(--text-primary)]">{dash(selected.target_direction)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Confidence</p><ConfidenceIndicator value={selected.confidence} /></div>
                    <div className="col-span-2"><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Created</p><p className="text-[var(--text-primary)]">{formatDateTime(selected.created_at)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div className="grid grid-cols-1 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Action plan</p><p className="text-[var(--text-primary)]" data-testid="detail-outcome-action-plan">{dash(planLabel(selected.action_plan_id))}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Recommendation</p><p className="text-[var(--text-primary)]" data-testid="detail-outcome-recommendation">{dash(recLabel(selected.recommendation_id))}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Opportunity</p><p className="text-[var(--text-primary)]" data-testid="detail-outcome-opportunity">{dash(oppLabel(selected.opportunity_id))}</p></div>
                  </div>
                  {Array.isArray(selected.guardrail_results) && selected.guardrail_results.length > 0 && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1.5">Guardrails</p>
                        <div className="space-y-1.5" data-testid="detail-outcome-guardrails">
                          {selected.guardrail_results.map((g, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] bg-[var(--surface-sunken,var(--canvas-secondary))] rounded px-2 py-1">
                              <span className="text-[var(--text-primary)] font-mono">{dash(g.metric)}</span>
                              <StatusBadge value={g.status} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Source measurement IDs</p>
                    <p className="text-[var(--text-tertiary)] font-mono break-all text-[10px]">{(selected.source_measurement_ids || []).join(', ') || '—'}</p>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
